var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { styled, alpha } from '@mui/material/styles';
// ----------------------------------------------------------------------
var StyledCalendar = styled('div')(function (_a) {
    var theme = _a.theme;
    return ({
        width: '100%',
        height: '100%',
        '& .fc': {
            '--fc-border-color': alpha(theme.customPalette.grey[500], 0.2),
            '--fc-now-indicator-color': theme.customPalette.error.main,
            '--fc-today-bg-color': theme.customPalette.action.selected,
            '--fc-page-bg-color': theme.customPalette.background.default,
            '--fc-neutral-bg-color': theme.customPalette.background.neutral,
            '--fc-list-event-hover-bg-color': theme.customPalette.action.hover,
            '--fc-highlight-color': theme.customPalette.action.hover,
            '--fc-event-border-color': theme.customPalette.info.dark,
            '--fc-list-event-dot-width': theme.spacing(1),
        },
        '& .fc .fc-license-message': { display: 'none' },
        '& .fc a': { color: theme.customPalette.text.primary },
        // Table
        '& .fc-theme-standard .fc-scrollgrid': {
            borderLeft: 'none',
        },
        '& .fc .fc-scrollgrid-section>*': {
            borderRightWidth: 0,
        },
        // Table Head
        '& .fc .fc-col-header ': {
            boxShadow: "inset 0 -1px 0 ".concat(theme.customPalette.divider),
            '& th': { borderColor: 'transparent' },
            '& .fc-col-header-cell-cushion': __assign(__assign({}, theme.customTypography.subtitle2), { padding: '13px 0' }),
        },
        // List Empty
        '& .fc .fc-list-empty': __assign(__assign({}, theme.customTypography.h6), { backgroundColor: 'transparent', color: theme.customPalette.text.secondary }),
        // Event
        '& .fc .fc-event': {
            borderColor: 'transparent !important',
            backgroundColor: 'transparent !important',
        },
        '& .fc .fc-event .fc-event-main': {
            padding: '2px 4px',
            borderRadius: 4,
            backgroundColor: theme.customPalette.common.white,
            color: theme.customPalette.info.dark,
            fontWeight: 500,
            transition: theme.transitions.create('filter'),
            '&:hover': { filter: 'brightness(0.96)' },
            '&:before,&:after': {
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                content: "''",
                borderRadius: 4,
                position: 'absolute',
                boxSizing: 'border-box',
            },
            '&:before': {
                zIndex: 8,
                opacity: 0.32,
                border: 'solid 1px currentColor',
            },
            '&:after': {
                zIndex: -1,
                opacity: 0.24,
                backgroundColor: 'currentColor',
            },
        },
        '& .fc .fc-event.fc-event-future .fc-event-main': {
            fontWeight: 400,
            '&:after': {
                opacity: 0.12,
            },
        },
        '& .fc .fc-event.fc-event-past .fc-event-main': {
            color: theme.customPalette.text.disabled,
            fontWeight: 400,
            '&:before': {
                opacity: 0.16,
                border: 'solid 1px currentColor',
            },
            '&:after': {
                opacity: 0.12,
                backgroundColor: 'currentColor',
            },
        },
        '& .fc .fc-event .fc-event-main-frame': {
            fontSize: 13,
            lineHeight: '20px',
        },
        '& .fc .fc-daygrid-event .fc-event-title': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
        '& .fc .fc-event .fc-event-time': {
            padding: 0,
            overflow: 'unset',
            fontWeight: theme.customTypography.fontWeightBold,
        },
        // Popover
        '& .fc .fc-popover': {
            border: 0,
            overflow: 'hidden',
            boxShadow: theme.customShadows.z20,
            borderRadius: theme.shape.borderRadius,
            backgroundColor: theme.customPalette.background.paper,
        },
        '& .fc .fc-popover-header': __assign(__assign({}, theme.customTypography.subtitle2), { padding: theme.spacing(1), backgroundColor: alpha(theme.customPalette.grey[500], 0.12), borderBottom: "solid 1px ".concat(theme.customPalette.divider) }),
        '& .fc .fc-popover-close': {
            opacity: 0.48,
            transition: theme.transitions.create('opacity'),
            '&:hover': { opacity: 1 },
        },
        '& .fc .fc-more-popover .fc-popover-body': {
            padding: theme.spacing(1.5),
        },
        '& .fc .fc-popover-body': {
            '& .fc-daygrid-event.fc-event-start, & .fc-daygrid-event.fc-event-end': {
                margin: '2px 0',
            },
        },
        // Month View
        '& .fc .fc-day-other .fc-daygrid-day-top': {
            opacity: 1,
            '& .fc-daygrid-day-number': {
                color: theme.customPalette.text.disabled,
            },
        },
        '& .fc .fc-daygrid-day-number': __assign(__assign({}, theme.customTypography.body2), { padding: theme.spacing(1, 1, 0) }),
        '& .fc .fc-daygrid-event': {
            marginTop: 4,
            marginLeft: 8,
            marginRight: 8,
        },
        '& .fc .fc-daygrid-event.fc-event-start, & .fc .fc-daygrid-event.fc-event-end': {
            marginLeft: 32,
            marginRight: 32,
        },
        '& .fc-direction-ltr .fc-daygrid-event.fc-event-end, .fc-direction-rtl .fc-daygrid-event.fc-event-start': {
            marginBottom: 0,
        },
        '& .fc .fc-daygrid-more-link': __assign(__assign({}, theme.customTypography.caption), { color: theme.customPalette.text.secondary }),
        // Agenda View
        '.fc-theme-standard .fc-list': {
            border: 'none',
        },
        '& .fc-direction-ltr .fc-list-day-text, .fc-direction-rtl .fc-list-day-side-text, .fc-direction-ltr .fc-list-day-side-text, .fc-direction-rtl .fc-list-day-text': __assign({}, theme.customTypography.subtitle2),
        '& .fc .fc-list-event': __assign(__assign({}, theme.customTypography.body2), { '& .fc-list-event-time': {
                color: theme.customPalette.text.secondary,
            } }),
        '& .fc .fc-list-table': {
            '& th, td': {
                borderColor: 'transparent',
            },
        },
        '& .fc .fc-list-sticky .fc-list-day>*': {
            boxShadow: '-1px 0 0 0 rgba(145, 158, 171, .16)',
        },
        '& .fc-theme-standard .fc-list-day-cushion': {
            backgroundColor: 'transparent',
            boxShadow: '0 1px 0 0 rgba(145, 158, 171, .16)',
        },
        '& .fc .fc-event-future .fc-list-event-dot': {
            opacity: 0.24,
        },
    });
});
export default StyledCalendar;
